// ==================== FLOATING CARD COMPONENT ====================

import { motion } from 'framer-motion';
import { cn } from '../../utils/helpers';
import {floatingCard } from '../../utils/animations';

interface FloatingCardProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  floatIntensity?: 'small' | 'medium' | 'large';
  floatSpeed?: 'slow' | 'normal' | 'fast';
  variant?: 'default' | 'glass' | 'solid' | 'gradient';
}

const FloatingCard = ({ 
  children, 
  className, 
  delay = 0,
  floatIntensity = 'medium',
  floatSpeed = 'normal',
  variant = 'default'
}: FloatingCardProps) => {
  // Float intensity values
  const intensityValues = {
    small: [-5, 5, -5],
    medium: [-10, 10, -10],
    large: [-15, 15, -15]
  };

  // Float speed durations
  const speedDurations = {
    slow: 6,
    normal: 4,
    fast: 2.5
  };

  // Variant styles
  const variantStyles = {
    default: 'bg-(--color-charcoal-black) border border-(--color-border-gray)',
    glass: 'bg-(--color-charcoal-black)/80 backdrop-blur-md border border-(--color-border-gray)/50',
    solid: 'bg-(--color-dark-navy) border border-(--color-border-gray)',
    gradient: 'bg-linear-to-br from-(--color-charcoal-black) to-(--color-dark-navy) border border-(--color-border-gray)'
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={floatingCard}
      transition={{ 
        delay,
        duration: 0.5,
        ease: [0.34, 1.56, 0.64, 1]
      }}
      className={cn(
        "rounded-2xl p-3 shadow-xl",
        variantStyles[variant],
        className
      )}
    >
      <motion.div 
        animate={{ y: intensityValues[floatIntensity] }}
        transition={{
          duration: speedDurations[floatSpeed],
          repeat: Infinity,
          ease: [0.45, 0.05, 0.55, 0.95]
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

// ==================== FLOATING CHAT BUBBLE ====================
// Specialized floating card for chat/notification bubbles

interface FloatingChatBubbleProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left' | 'right';
}

const FloatingChatBubble = ({ 
  children, 
  className, 
  delay = 0,
  position
}: FloatingChatBubbleProps) => {
  // Position classes
  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'left': 'left-4 top-1/2 -translate-y-1/2',
    'right': 'right-4 top-1/2 -translate-y-1/2'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        scale: 1, 
        y: [-10, 10, -10],
      }}
      transition={{ 
        opacity: { delay, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] },
        scale: { delay, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] },
        y: { 
          delay: delay + 0.5,
          duration: 4,
          repeat: Infinity,
          ease: [0.45, 0.05, 0.55, 0.95]
        }
      }}
      className={cn(
        "absolute bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-2xl p-3 shadow-xl z-20",
        position && positionClasses[position],
        className
      )}
    >
      {children}
    </motion.div>
  );
};

// ==================== FLOATING STAT CARD ====================
// For displaying statistics with floating animation

interface FloatingStatCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  className?: string;
  delay?: number;
  iconBgColor?: string;
  iconColor?: string;
}

const FloatingStatCard = ({ 
  icon, 
  value, 
  label, 
  className, 
  delay = 0,
  iconBgColor = 'bg-(--color-success)/20',
  iconColor = 'text-(--color-success)'
}: FloatingStatCardProps) => {
  return (
    <FloatingChatBubble className={className} delay={delay}>
      <div className="flex items-center gap-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", iconBgColor)}>
          <span className={iconColor}>{icon}</span>
        </div>
        <div>
          <p className="text-lg font-bold text-white">{value}</p>
          <p className="text-[10px] text-(--color-muted-gray)">{label}</p>
        </div>
      </div>
    </FloatingChatBubble>
  );
};

// ==================== FLOATING NOTIFICATION CARD ====================
// For displaying notification-style floating cards

interface FloatingNotificationCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  badge?: React.ReactNode;
  className?: string;
  delay?: number;
  iconBgColor?: string;
}

const FloatingNotificationCard = ({ 
  icon, 
  title, 
  description,
  badge,
  className, 
  delay = 0,
  iconBgColor = 'bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber)'
}: FloatingNotificationCardProps) => {
  return (
    <FloatingChatBubble className={className} delay={delay}>
      <div className="flex items-center gap-2">
        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", iconBgColor)}>
          {icon}
        </div>
        <div>
          <p className="text-xs font-semibold text-white">{title}</p>
          {description && (
            <p className="text-[10px] text-(--color-muted-gray)">{description}</p>
          )}
          {badge && (
            <div className="flex items-center gap-1 mt-0.5">
              {badge}
            </div>
          )}
        </div>
      </div>
    </FloatingChatBubble>
  );
};

export { 
  FloatingCard, 
  FloatingChatBubble, 
  FloatingStatCard, 
  FloatingNotificationCard 
};

